<template>
  <div class="page">
    <div class="page-title">角色菜单</div>
    <div class="notice-bar margin-bottom-12">
      <i class="fa fa-key notice-icon"></i>
      <span>维护 org-auth 自家菜单注册 + 角色，以及「角色↔菜单」「角色↔用户」绑定。组织管理员须到组织结构成员列表设置，以明确管理范围。</span>
    </div>

    <!-- 自定义页签 -->
    <div class="tabs">
      <div class="tab" :class="{ active: tab === 'menu' }" @click="tab = 'menu'">菜单管理</div>
      <div class="tab" :class="{ active: tab === 'role' }" @click="tab = 'role'">角色管理</div>
    </div>

    <!-- ============ 菜单管理 ============ -->
    <div class="panel" v-show="tab === 'menu'">
      <div class="toolbar space-between">
        <span class="panel-title">菜单注册（{{ menus.length }}）</span>
        <Button color="primary" icon="fa fa-plus" @click="openMenuCreate">新建菜单</Button>
      </div>
      <Table :datas="menus" :loading="loadingMenu">
        <TableItem title="名称" :width="200">
          <template slot-scope="{data}">
            <i :class="data.icon" style="width:16px;color:#6b7280"></i>
            {{ data.name }}
          </template>
        </TableItem>
        <TableItem title="类型" :width="90">
          <template slot-scope="{data}">
            <Tag :color="data.type === 'app' ? 'blue' : 'primary'">{{ data.type === 'app' ? '应用' : '管理' }}</Tag>
          </template>
        </TableItem>
        <TableItem title="路由/路径" prop="route"></TableItem>
        <TableItem title="排序" prop="sort" :width="70"></TableItem>
        <TableItem title="权限码" prop="perm" :width="120"></TableItem>
        <TableItem title="操作" :width="140">
          <template slot-scope="{data}">
            <Button size="xs" @click="openMenuEdit(data)">编辑</Button>
            <Button size="xs" color="red" @click="removeMenu(data)">删除</Button>
          </template>
        </TableItem>
      </Table>
      <div v-if="!loadingMenu && !menus.length" class="empty-tip">暂无菜单</div>
    </div>

    <!-- ============ 角色管理 ============ -->
    <div class="panel" v-show="tab === 'role'">
      <div class="toolbar space-between">
        <span class="panel-title">角色（{{ roles.length }}）</span>
        <Button color="primary" icon="fa fa-plus" @click="openRoleCreate">新建角色</Button>
      </div>
      <Table :datas="roles" :loading="loadingRole">
        <TableItem title="角色名" :width="160">
          <template slot-scope="{data}">
            {{ data.name }}
            <Tag v-if="data.builtin" color="gray">内置</Tag>
          </template>
        </TableItem>
        <TableItem title="key" prop="key" :width="140"></TableItem>
        <TableItem title="描述" prop="desc"></TableItem>
        <TableItem title="菜单数" prop="menuCount" :width="80"></TableItem>
        <TableItem title="成员数" prop="userCount" :width="80"></TableItem>
        <TableItem title="操作" :width="300">
          <template slot-scope="{data}">
            <Button size="xs" @click="openMenuConfig(data)" :disabled="data.key === 'platform_admin'">配置菜单</Button>
            <Button
              size="xs"
              @click="openMemberAssign(data)"
              :disabled="data.key === 'org_admin'"
              :title="data.key === 'org_admin' ? '请到组织结构成员列表中设置组织管理员' : ''"
            >分配成员</Button>
            <Button size="xs" @click="openRoleEdit(data)">编辑</Button>
            <Button size="xs" color="red" @click="removeRole(data)" :disabled="data.builtin">删除</Button>
          </template>
        </TableItem>
      </Table>
      <div v-if="!loadingRole && !roles.length" class="empty-tip">暂无角色</div>
    </div>

    <!-- 菜单新建/编辑 -->
    <Modal v-model="menuModal.show" :width="460" :closeOnMask="false">
      <template #header>{{ menuModal.mode === 'create' ? '新建菜单' : '编辑菜单' }}</template>
      <Form class="std-form" :model="menuModal.form" :label-width="92">
        <FormItem label="菜单ID" class="req-label">
          <input class="form-input" v-model="menuModal.form.id" :disabled="menuModal.mode === 'edit'" placeholder="应用/路由唯一标识，如 finance / OrgList" />
        </FormItem>
        <FormItem label="类型" class="req-label">
          <Radio v-model="menuModal.form.type" :datas="[{key:'app',title:'应用（门户卡片）'},{key:'admin',title:'管理（左侧菜单）'}]" />
        </FormItem>
        <FormItem label="名称" class="req-label">
          <input class="form-input" v-model="menuModal.form.name" placeholder="显示名称" />
        </FormItem>
        <FormItem label="路由/路径">
          <input class="form-input" v-model="menuModal.form.route" placeholder="如 /portal/finance；建设中系统留空" />
        </FormItem>
        <FormItem label="图标">
          <input class="form-input" v-model="menuModal.form.icon" placeholder="如 fa fa-cny" />
        </FormItem>
        <FormItem label="排序">
          <input class="form-input" type="number" v-model.number="menuModal.form.sort" placeholder="数字越小越靠前" />
        </FormItem>
        <FormItem label="权限码">
          <input class="form-input" v-model="menuModal.form.perm" placeholder="选填，路由守卫用" />
        </FormItem>
      </Form>
      <template #footer>
        <Button @click="menuModal.show = false">取消</Button>
        <Button color="primary" :loading="menuModal.saving" @click="saveMenu">确定</Button>
      </template>
    </Modal>

    <!-- 角色新建/编辑 -->
    <Modal v-model="roleModal.show" :width="460" :closeOnMask="false">
      <template #header>{{ roleModal.mode === 'create' ? '新建角色' : '编辑角色' }}</template>
      <Form class="std-form" :model="roleModal.form" :label-width="92">
        <FormItem label="角色Key" class="req-label">
          <input class="form-input" v-model="roleModal.form.key" :disabled="roleModal.mode === 'edit'" placeholder="英文唯一标识，如 finance_admin" />
        </FormItem>
        <FormItem label="角色名" class="req-label">
          <input class="form-input" v-model="roleModal.form.name" placeholder="显示名称" />
        </FormItem>
        <FormItem label="描述">
          <input class="form-input" v-model="roleModal.form.desc" placeholder="选填" />
        </FormItem>
      </Form>
      <template #footer>
        <Button @click="roleModal.show = false">取消</Button>
        <Button color="primary" :loading="roleModal.saving" @click="saveRole">确定</Button>
      </template>
    </Modal>

    <!-- 配置菜单 -->
    <Modal v-model="menuConfig.show" :width="520" :closeOnMask="false">
      <template #header>配置菜单 · {{ menuConfig.roleName }}</template>
      <div class="menu-config">
        <div class="cfg-group">
          <div class="cfg-head">应用入口</div>
          <div class="cfg-items">
            <label class="cfg-item" v-for="m in appMenus" :key="m.id">
              <Checkbox v-model="menuConfig.checked[m.id]"></Checkbox>
              <i :class="m.icon"></i>{{ m.name }}
            </label>
          </div>
        </div>
        <div class="cfg-group">
          <div class="cfg-head">管理菜单</div>
          <div class="cfg-items">
            <label class="cfg-item" v-for="m in adminMenus" :key="m.id">
              <Checkbox v-model="menuConfig.checked[m.id]"></Checkbox>
              <i :class="m.icon"></i>{{ m.name }}
            </label>
          </div>
        </div>
      </div>
      <template #footer>
        <Button @click="menuConfig.show = false">取消</Button>
        <Button color="primary" :loading="menuConfig.saving" @click="saveMenuConfig">保存</Button>
      </template>
    </Modal>

    <!-- 分配成员：已有成员 + 搜索加人，不拉全站用户 -->
    <Modal v-model="member.show" :width="520" :closeOnMask="false">
      <template #header>分配成员 · {{ member.roleName }}</template>
      <div class="member-add">
        <UserPicker v-model="member.addUserId" placeholder="输入姓名/手机号搜索加人" @change="onMemberPick" />
      </div>
      <div class="member-list">
        <div class="cfg-item member-row" v-for="u in member.users" :key="u.id">
          <span class="m-name">{{ u.realName }}</span>
          <span class="m-mobile muted">{{ u.mobile }}</span>
          <Button size="xs" color="red" @click="removeMember(u)">移除</Button>
        </div>
        <div v-if="!member.users.length" class="empty-tip">暂无成员，请搜索加人</div>
      </div>
      <template #footer>
        <Button @click="member.show = false">取消</Button>
        <Button color="primary" :loading="member.saving" @click="saveMember">保存</Button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { menuApi, roleApi } from '../api';

export default {
	name: 'RoleMenu',
	data() {
		return {
			tab: 'menu',
			// 菜单
			menus: [],
			loadingMenu: false,
			menuModal: { show: false, mode: 'create', saving: false, form: this.emptyMenuForm() },
			// 角色
			roles: [],
			loadingRole: false,
			roleModal: { show: false, mode: 'create', saving: false, form: this.emptyRoleForm() },
			// 配置菜单
			menuConfig: { show: false, key: '', roleName: '', checked: {}, saving: false },
			// 分配成员（users = 当前集合；不预拉全站）
			member: { show: false, key: '', roleName: '', users: [], addUserId: null, saving: false }
		};
	},
	computed: {
		appMenus() { return this.menus.filter(m => m.type === 'app'); },
		adminMenus() { return this.menus.filter(m => m.type === 'admin'); }
	},
	created() {
		this.loadMenus();
		this.loadRoles();
	},
	methods: {
		emptyMenuForm() { return { id: '', type: 'admin', name: '', route: '', icon: 'fa fa-circle', sort: 99, perm: '' }; },
		emptyRoleForm() { return { key: '', name: '', desc: '' }; },

		// ---- 菜单 ----
		async loadMenus() {
			this.loadingMenu = true;
			try { this.menus = await menuApi.list(); }
			catch (e) { this.$Message.error(e.message); }
			finally { this.loadingMenu = false; }
		},
		openMenuCreate() {
			this.menuModal = { show: true, mode: 'create', saving: false, form: this.emptyMenuForm() };
		},
		openMenuEdit(m) {
			this.menuModal = { show: true, mode: 'edit', saving: false, form: { ...m } };
		},
		async saveMenu() {
			const f = this.menuModal.form;
			if (!f.id || !f.name) { this.$Message.error('菜单ID与名称必填'); return; }
			this.menuModal.saving = true;
			try {
				if (this.menuModal.mode === 'create') await menuApi.create({ ...f });
				else await menuApi.update(f.id, { name: f.name, route: f.route, icon: f.icon, sort: f.sort, perm: f.perm, type: f.type });
				this.$Message.success('已保存');
				this.menuModal.show = false;
				await this.loadMenus();
			} catch (e) { this.$Message.error(e.message); }
			finally { this.menuModal.saving = false; }
		},
		removeMenu(m) {
			this.$Confirm(`确认删除菜单「${m.name}」？将从所有角色绑定中移除。`, '删除菜单').then(async () => {
				try { await menuApi.remove(m.id); this.$Message.success('已删除'); await this.loadMenus(); }
				catch (e) { this.$Message.error(e.message); }
			}).catch(() => {});
		},

		// ---- 角色 ----
		async loadRoles() {
			this.loadingRole = true;
			try { this.roles = await roleApi.list(); }
			catch (e) { this.$Message.error(e.message); }
			finally { this.loadingRole = false; }
		},
		openRoleCreate() {
			this.roleModal = { show: true, mode: 'create', saving: false, form: this.emptyRoleForm() };
		},
		openRoleEdit(r) {
			this.roleModal = { show: true, mode: 'edit', saving: false, form: { key: r.key, name: r.name, desc: r.desc } };
		},
		async saveRole() {
			const f = this.roleModal.form;
			if (!f.key || !f.name) { this.$Message.error('角色Key与名称必填'); return; }
			this.roleModal.saving = true;
			try {
				if (this.roleModal.mode === 'create') await roleApi.create({ ...f });
				else await roleApi.update(f.key, { name: f.name, desc: f.desc });
				this.$Message.success('已保存');
				this.roleModal.show = false;
				await this.loadRoles();
			} catch (e) { this.$Message.error(e.message); }
			finally { this.roleModal.saving = false; }
		},
		removeRole(r) {
			this.$Confirm(`确认删除角色「${r.name}」？成员将回到默认 employee。`, '删除角色').then(async () => {
				try { await roleApi.remove(r.key); this.$Message.success('已删除'); await this.loadRoles(); }
				catch (e) { this.$Message.error(e.message); }
			}).catch(() => {});
		},

		// ---- 角色↔菜单 ----
		async openMenuConfig(r) {
			if (r.key === 'platform_admin') { this.$Message.error('平台管理员始终拥有全部菜单，绑定不可改'); return; }
			this.menuConfig = { show: true, key: r.key, roleName: r.name, checked: {}, saving: false };
			const ids = await roleApi.menus(r.key);
			const checked = {};
			this.menus.forEach(m => { checked[m.id] = ids.includes(m.id); });
			this.menuConfig.checked = checked;
		},
		async saveMenuConfig() {
			this.menuConfig.saving = true;
			const ids = this.menus.filter(m => this.menuConfig.checked[m.id]).map(m => m.id);
			try {
				await roleApi.setMenus(this.menuConfig.key, ids);
				this.$Message.success('已保存');
				this.menuConfig.show = false;
				await this.loadRoles();
			} catch (e) { this.$Message.error(e.message); }
			finally { this.menuConfig.saving = false; }
		},

		// ---- 角色↔用户 ----
		async openMemberAssign(r) {
			if (r.key === 'org_admin') {
				this.$Message.error('请到组织结构成员列表中设置组织管理员，以明确管理范围');
				return;
			}
			this.member = { show: true, key: r.key, roleName: r.name, users: [], addUserId: null, saving: false };
			const rows = await roleApi.users(r.key);
			this.member.users = (Array.isArray(rows) ? rows : []).map((u) => ({
				id: u.id,
				realName: u.realName,
				mobile: u.mobile
			}));
		},
		onMemberPick(u) {
			this.member.addUserId = null;
			if (!u || u.id == null) return;
			if (this.member.users.some((x) => x.id === u.id)) {
				this.$Message.info('已在成员列表中');
				return;
			}
			this.member.users.push({ id: u.id, realName: u.realName, mobile: u.mobile });
		},
		removeMember(u) {
			this.member.users = this.member.users.filter((x) => x.id !== u.id);
		},
		async saveMember() {
			this.member.saving = true;
			const ids = this.member.users.map((u) => u.id);
			try {
				await roleApi.setUsers(this.member.key, ids);
				this.$Message.success('已保存');
				this.member.show = false;
				await this.loadRoles();
			} catch (e) { this.$Message.error(e.message); }
			finally { this.member.saving = false; }
		}
	}
};
</script>

<style lang="less" scoped>
.tabs {
	display: flex;
	gap: 4px;
	margin-bottom: 12px;
	border-bottom: 1px solid #eef0f3;
}
.tab {
	padding: 8px 16px;
	font-size: 14px;
	color: #6b7280;
	cursor: pointer;
	border-bottom: 2px solid transparent;
	&:hover { color: #3788ee; }
	&.active { color: #3788ee; font-weight: 600; border-bottom-color: #3788ee; }
}
.panel { background: #fff; border-radius: 6px; padding: 12px; }
.panel-title { font-size: 14px; font-weight: 600; color: #4b5563; }

/* 配置菜单 / 分配成员 */
.menu-config, .member-list { max-height: 380px; overflow-y: auto; }
.member-add { margin-bottom: 12px; }
.member-row { justify-content: space-between; cursor: default; }
.cfg-group { margin-bottom: 14px; }
.cfg-head { font-size: 12px; font-weight: 600; color: #9aa4b2; margin-bottom: 6px; }
.cfg-items { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; }
.cfg-item {
	display: flex; align-items: center; gap: 6px;
	font-size: 13px; color: #374151; cursor: pointer; padding: 4px 0;
	i { width: 16px; color: #6b7280; }
}
.m-name { font-weight: 500; }
.m-mobile { margin-left: auto; font-size: 12px; }
.muted { color: #9aa4b2; }
</style>
